import { Controller } from "@hotwired/stimulus"

// Drives the two things the OneDrive configuration section cannot do server-side:
// a live preview of the folder-name pattern as it is typed, and an on-demand table
// showing what applying the SAVED pattern would do to folders that already exist.
//
// Half of what this used to do is gone, because the integration page already does it:
// enabling/disabling is the header's Aktivieren button, testing the connection is the
// header's Verbindung testen (which persists its result rather than dropping it into a
// span), and applying the schema is a real :action operation with a confirmation and a
// run-history entry.
// A folder name as SharePoint stores it, case aside — Onedrive::ItemName.sanitize: forbidden
// characters become "_", leading and trailing dots and spaces go. "Archiv", "archiv." and
// "A/B" vs "A_B" are one folder there, so they are one folder here.
const sharePointName = (name) =>
  name
    .replace(/[<>:"/\\|?*]/g, "_")
    .replace(/^[\s.]+|[\s.]+$/g, "")
    .slice(0, 400)
    .toLowerCase()
const sameName = (a, b) => sharePointName(a) === sharePointName(b)

export default class extends Controller {
  static targets = [
    "pattern",
    "folderPreview",
    "schemaPreviewButton",
    "schemaPreviewContainer",
    "schemaUnsavedHint",
    "lifecycleEnabled",
    "lifecycleFields",
    "activeSitesFolderName",
    "lifecycleFolderName",
    "statusFolderList",
    "statusFolderTemplate",
    "structure",
  ]

  static values = {
    requestError: String,
    savedPattern: String,
    integrationId: Number,
    // The submit button of the "Schema anwenden" operation card. That card is a SIBLING
    // of this controller's element — an operation card carries its own form and cannot be
    // nested inside the configuration form — so it is out of reach of a Stimulus target
    // and has to be found by id. OperationCardComponent renders `<operation key>_submit`.
    applyButtonId: String,
    // The settings that shape the folder tree (Integrations::Onedrive::Settings::STRUCTURE_KEYS).
    // Only these go into the preview's query string: the form also carries the client
    // secret, which must never end up in a URL.
    structureKeys: Array,
  }

  connect() {
    this.previewRequestId = 0
    // The card can be disabled server-side too (Integration#operation_available?). Keep
    // that state so the unsaved-pattern guard below can only ever ADD a reason to be
    // disabled, never re-enable a button the server greyed out.
    this.applyButtonInitiallyDisabled = this.applyButton?.disabled ?? false
    this.connectStatusFolders()
    this.updatePreview()
    this.structureRequestId = 0
    this.refreshStructure()
  }

  disconnect() {
    clearTimeout(this.structureTimer)
  }

  // Every keystroke in any folder setting redraws the tree, debounced so typing a name
  // costs one request, not one per letter.
  scheduleStructure() {
    clearTimeout(this.structureTimer)
    this.structureTimer = setTimeout(() => this.refreshStructure(), 250)
  }

  // Newest response wins, like #updatePreview.
  async refreshStructure() {
    if (!this.hasStructureTarget) return

    const requestId = ++this.structureRequestId
    try {
      const response = await fetch(`${this.baseUrl}/structure_preview?${this.structureQuery}`, {
        headers: this.headers,
      })
      const html = response.ok ? await response.text() : null
      if (requestId !== this.structureRequestId) return

      if (html === null) {
        this.structureTarget.textContent = this.requestErrorValue
      } else {
        this.structureTarget.innerHTML = html
      }
    } catch (_error) {
      if (requestId === this.structureRequestId)
        this.structureTarget.textContent = this.requestErrorValue
    }
  }

  // A checkbox's hidden "0" partner shares its name, so the checkbox itself decides.
  get structureQuery() {
    const query = new URLSearchParams()
    this.structureKeysValue.forEach((key) => {
      const inputs = [...this.element.querySelectorAll(`[name="integration[settings][${key}]"]`)]
      const checkbox = inputs.find((input) => input.type === "checkbox")
      const radio = inputs.find((input) => input.type === "radio" && input.checked)
      const text = inputs.find((input) => !["checkbox", "radio", "hidden"].includes(input.type))
      if (checkbox) query.set(`settings[${key}]`, checkbox.checked ? "1" : "0")
      else if (radio) query.set(`settings[${key}]`, radio.value)
      else if (text) query.set(`settings[${key}]`, text.value)
    })
    return query
  }

  // One request per keystroke, and responses are not guaranteed to come back in the order
  // they were sent — so a slower earlier request could land last and leave the preview
  // showing a pattern the operator has already typed past. Observed live: typing "XYZ" left
  // the preview reading "XY" and it stayed there. Only the newest response may paint.
  async updatePreview() {
    if (!this.hasPatternTarget || !this.hasFolderPreviewTarget) return

    const pattern = this.patternTarget.value || this.savedPatternValue
    const requestId = ++this.previewRequestId
    const query = new URLSearchParams({ pattern, lifecycle_folder: this.activeLifecycleFolder })
    this.toggleLifecycleFields()

    try {
      const response = await fetch(`${this.baseUrl}/folder_name_preview?${query}`, {
        headers: this.headers,
      })

      const text = response.ok ? await response.text() : null
      if (text !== null && requestId === this.previewRequestId) {
        this.folderPreviewTarget.textContent = text
      }
    } catch (error) {
      console.error("Failed to update preview:", error)
    }

    this.toggleSchemaButton()
  }

  // Both the preview and the apply button act on the SAVED pattern and status folders, so
  // while an input differs from them both are disabled and the hint says why.
  //
  // The apply button matters more than the preview here: it renames every linked folder
  // in the tenant's SharePoint. Left enabled next to an edited field it reads as "apply
  // what I am looking at" and does something else — it would apply the pattern still in
  // the database. Until the move onto the integration page the guard reached it by
  // accident, because applying was only possible from inside the loaded preview table.
  toggleSchemaButton() {
    if (!this.hasPatternTarget) return

    const unsaved = this.placement(false) !== this.placement(true)

    if (this.hasSchemaPreviewButtonTarget) {
      this.schemaPreviewButtonTarget.disabled = unsaved
    }

    const applyButton = this.applyButton
    if (applyButton) {
      applyButton.disabled = this.applyButtonInitiallyDisabled || unsaved
      applyButton.classList.toggle("bg-gray-300", applyButton.disabled)
      applyButton.classList.toggle("text-gray-600", applyButton.disabled)
      applyButton.classList.toggle("cursor-not-allowed", applyButton.disabled)
      applyButton.classList.toggle("bg-green-500", !applyButton.disabled)
      applyButton.classList.toggle("hover:bg-green-600", !applyButton.disabled)
      applyButton.classList.toggle("text-white", !applyButton.disabled)
    }

    if (this.hasSchemaUnsavedHintTarget) {
      this.schemaUnsavedHintTarget.classList.toggle("hidden", !unsaved)
    }
  }

  // Everything that decides where a site folder goes, as one comparable string — as typed,
  // or as saved. The saved side reads what the server rendered (defaultValue/defaultChecked,
  // the HTML attributes), never the inputs at connect: a browser restoring the form on
  // "back" would otherwise make its restored edits the baseline and hide them as saved.
  placement(saved) {
    const field = (name) => {
      const input = this.targets.find(name)
      if (!input) return ""
      if (input.type === "checkbox") return saved ? input.defaultChecked : input.checked

      return saved ? input.defaultValue : input.value
    }

    // The status selects: a <select> has no defaultValue, so the saved one rides along as
    // data-saved-value.
    const folderNames = this.lifecycleFolderNameTargets.map((select) =>
      saved ? (select.dataset.savedValue ?? "") : select.value,
    )

    return JSON.stringify([
      saved ? this.savedPatternValue || "" : this.patternTarget.value || "",
      field("lifecycleEnabled"),
      ...folderNames,
    ])
  }

  // The preview shows an active site, so it leads with the active status's folder — none
  // when that status is mapped to "Kein Statusordner" ("").
  get activeLifecycleFolder() {
    if (!this.hasLifecycleEnabledTarget || !this.lifecycleEnabledTarget.checked) return ""
    if (!this.hasActiveSitesFolderNameTarget) return ""

    return this.activeSitesFolderNameTarget.value
  }

  // --- Status folders and the status → folder mapping ---------------------------------
  //
  // The folder rows are not submitted; each status select posts the NAME of the folder it
  // points to. A select remembers WHICH row it points to (data-folder-key), so retyping a
  // folder's name carries its statuses along, even past a half-typed name that briefly
  // matches another folder. A row's data-name is its last non-blank name: while the input
  // is cleared to be retyped, its statuses stay with it. Names are compared as SharePoint
  // stores them (sharePointName). The selects follow a rename once the name settles (blur or
  // Enter), not on every keystroke: each sync rebuilds every SlimSelect and the preview.

  connectStatusFolders() {
    this.statusFolderSequence = 0
    this.statusFolderRows.forEach((row) => this.keyStatusFolder(row))
    this.lifecycleFolderNameTargets.forEach((select) => this.pointStatusAt(select, select.value))
    this.updateStatusFolderButtons()
  }

  keyStatusFolder(row) {
    row.dataset.key = String(++this.statusFolderSequence)
  }

  // The first row carrying +name+, or none ("Kein Statusordner").
  pointStatusAt(select, name) {
    const row =
      name === "" ? null : this.statusFolderRows.find((r) => sameName(r.dataset.name || "", name))
    select.dataset.folderKey = row ? row.dataset.key : ""
  }

  // The admin picked a folder for a status. SlimSelect also fires change when it rebuilds
  // the options; a select still on its row's name keeps that row, so two rows briefly
  // sharing a name mid-typing cannot swap their statuses.
  pickStatusFolder(event) {
    const select = event.currentTarget
    const row = this.statusFolderRows.find((r) => r.dataset.key === select.dataset.folderKey)
    if (row && sameName(row.dataset.name || "", select.value)) return

    this.pointStatusAt(select, select.value)
    this.updateStatusFolderButtons()
  }

  addStatusFolder() {
    if (!this.hasStatusFolderTemplateTarget || !this.hasStatusFolderListTarget) return

    this.statusFolderListTarget.append(this.statusFolderTemplateTarget.content.cloneNode(true))
    const rows = this.statusFolderRows
    const row = rows[rows.length - 1]
    this.keyStatusFolder(row)
    row.querySelector("input").focus()
    this.updateStatusFolderButtons()
  }

  renameStatusFolder(event) {
    const row = event.target.closest("[data-role='status-folder']")
    const name = event.target.value.trim()
    if (!row || name === "") return

    row.dataset.name = name
  }

  // On leaving a row: a cleared one gets its last name back (or goes, if it never had one),
  // and one that now repeats another row's name merges into that row.
  settleStatusFolder(event) {
    const row = event.target.closest("[data-role='status-folder']")
    if (!row) return

    const input = row.querySelector("input")
    const name = row.dataset.name || ""
    if (input.value.trim() === "") {
      if (name === "") {
        row.remove()
        this.updateStatusFolderButtons()
        return
      }
      input.value = name
    }

    const twin = this.statusFolderRows.find(
      (other) => other !== row && sameName(other.dataset.name || "", name),
    )
    if (twin) {
      this.lifecycleFolderNameTargets.forEach((select) => {
        if (select.dataset.folderKey === row.dataset.key)
          select.dataset.folderKey = twin.dataset.key
      })
      row.remove()
    }
    this.syncStatusSelects()
  }

  removeStatusFolder(event) {
    const row = event.currentTarget.closest("[data-role='status-folder']")
    if (!row || this.statusFolderInUse(row)) return

    row.remove()
    this.syncStatusSelects()
  }

  get statusFolderRows() {
    if (!this.hasStatusFolderListTarget) return []

    return [...this.statusFolderListTarget.querySelectorAll("[data-role='status-folder']")]
  }

  // Distinct names, in row order — the options every status select offers.
  get statusFolderNames() {
    return this.statusFolderRows
      .map((row) => row.dataset.name || "")
      .filter(
        (name, index, names) =>
          name !== "" && names.findIndex((other) => sameName(other, name)) === index,
      )
  }

  statusFolderInUse(row) {
    return this.lifecycleFolderNameTargets.some(
      (select) => select.dataset.folderKey && select.dataset.folderKey === row.dataset.key,
    )
  }

  // Rebuilds every status select's options from the folder rows and selects the name of the
  // row it points to. The "Kein Statusordner" option keeps its rendered label.
  syncStatusSelects() {
    const names = this.statusFolderNames
    this.lifecycleFolderNameTargets.forEach((select) => {
      const row = this.statusFolderRows.find((r) => r.dataset.key === select.dataset.folderKey)
      const target = row ? names.find((name) => sameName(name, row.dataset.name || "")) : ""
      const noneLabel = [...select.options].find((option) => option.value === "")?.textContent
      select.replaceChildren(
        ...names.map((name) => new Option(name, name, false, name === target)),
        new Option(noneLabel ?? "", "", false, target === ""),
      )
      select.value = target ?? ""
      this.application.getControllerForElementAndIdentifier(select, "select")?.refresh()
    })
    this.updateStatusFolderButtons()
    this.updatePreview()
    this.scheduleStructure()
  }

  // A folder a status still points to cannot be removed — the status would silently lose
  // its folder. The button says why.
  updateStatusFolderButtons() {
    this.statusFolderRows.forEach((row) => {
      const button = row.querySelector("button")
      if (!button) return

      const inUse = this.statusFolderInUse(row)
      button.disabled = inUse
      if (inUse) button.title = button.dataset.inUseTitle || ""
      else button.removeAttribute("title")
    })
  }

  // A placeholder button under a pattern field: insert its token at that field's cursor.
  // The input event lets the field's own listeners (pattern preview, structure tree) follow.
  insertToken(event) {
    const { token, inputId } = event.currentTarget.dataset
    const input = inputId && document.getElementById(inputId)
    if (!input || !token) return

    const start = input.selectionStart ?? input.value.length
    const end = input.selectionEnd ?? input.value.length
    input.value = input.value.slice(0, start) + token + input.value.slice(end)
    const caret = start + token.length
    input.focus()
    input.setSelectionRange(caret, caret)
    input.dispatchEvent(new Event("input", { bubbles: true }))
  }

  toggleLifecycleFields() {
    if (!this.hasLifecycleFieldsTarget || !this.hasLifecycleEnabledTarget) return

    this.lifecycleFieldsTarget.classList.toggle("hidden", !this.lifecycleEnabledTarget.checked)
  }

  get applyButton() {
    if (!this.hasApplyButtonIdValue) return null

    return document.getElementById(this.applyButtonIdValue)
  }

  async loadSchemaPreview() {
    try {
      const response = await fetch(`${this.baseUrl}/folder_schema_preview`, {
        headers: this.headers,
      })

      if (response.ok) {
        this.schemaPreviewContainerTarget.innerHTML = await response.text()
      } else {
        this.schemaPreviewContainerTarget.textContent = this.requestErrorValue
      }
    } catch (_error) {
      this.schemaPreviewContainerTarget.textContent = this.requestErrorValue
    }
  }

  closeSchemaPreview() {
    this.schemaPreviewContainerTarget.innerHTML = ""
  }

  get baseUrl() {
    return `/integrations/${this.integrationIdValue}/onedrive`
  }

  get headers() {
    return { "X-CSRF-Token": document.querySelector('meta[name="csrf-token"]').content }
  }
}
